/**
 * What: The Study Builder form and panel. It mounts only once the page lifecycle is ready, keyed on the visit
 *   and the applied-data revision, so its form always starts from checked data (no effect copies data in).
 * Transport / data-flow: Checked builder state and a finished account/history sidebar read. Its query exists only
 *   while the editor mounts and its selection changes only with the server estimate, never ordinary typing.
 */
import { DirectStudyDeleteControl } from '../direct-studies/DirectStudyDeleteControl'
import { Banner } from './Banner'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { useAppBridge } from '@shopify/app-bridge-react'
import { StudyBuilderSaveBar } from './StudyBuilderSaveBar'
import { UnifiedStudySetupPanel } from './UnifiedStudySetupPanel'
import { StudyBuilderGuidance } from './StudyBuilderGuidance'
import { StudyBuilderResearch } from './StudyBuilderResearch'
import { NextTestDraftSource } from './NextTestDraftSource'
import { useStudyBuilderSidebarQuery } from '../../hooks/study-builder/useStudyBuilderSidebarQuery'
import { ShopifyBackActionButton } from '../ShopifyPageActionButtons'
import { buildCalendlyBookingUrl, buildDirectStudyResultsViewUrl } from '../../resultsNavigation'
import type { ShopifyAppViewState } from '../../resultsNavigation'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import { useShopifyStudyBuilder } from '../../hooks/study-builder'
import type { StudyBuilderLifecycle } from '../../hooks/study-builder/studyBuilderLifecycle'
import type { StudyBuilderStart } from '../../hooks/study-builder/studyBuilderStart'
import type { StudySetup } from '../../types'

export function StudyBuilderEditor({
  appView,
  visit,
  shopify,
  scope,
  readiness,
  start,
  lifecycle,
  onDraftSaved,
  onSetupSaved,
  onStudyPrepared,
  onContactStudySupport,
  onDeleted,
  onBack,
}: {
  appView: Extract<ShopifyAppViewState, { view: 'study-builder' }>
  visit: string
  shopify: ReturnType<typeof useAppBridge>
  scope: VerifiedTestFeedConnectionScope
  readiness: {
    code: string
    builderLoadError?:
      | import('../../../src/shared/testfeed-connection-status-contract').BuilderLoadErrorDictionary
      | null
  } | null
  start: StudyBuilderStart
  lifecycle: StudyBuilderLifecycle
  onDraftSaved: (record?: import('@testfeed/shopify-contracts').StudyAuthoringRecord) => void
  onStudyPrepared?: (studyId: string) => void
  onSetupSaved?: (setup: StudySetup) => void
  onContactStudySupport?: (setup: StudySetup) => void
  onDeleted: () => void
  onBack: () => void
}) {
  const builder = useShopifyStudyBuilder(shopify, {
    visit,
    scope,
    readiness,
    start,
    lifecycle,
    editSetupId: appView.setupId?.trim() || null,
    draftId: appView.studyId ?? appView.draftId ?? '',
    forkParentPacketId: appView.forkParentPacketId ?? null,
    forkChangeReason: appView.forkChangeReason ?? null,
    onDraftSaved,
    onSetupSaved,
    onStudyPrepared,
  })
  const sidebar = useStudyBuilderSidebarQuery(shopify, builder.evaluation.projection?.summary.shoppers.creditEstimate)

  const directState = builder.authoring.state
  const directRecord = builder.authoring.record
  // The banner's tone, heading (the status label) and action are all server-decided; the app only wires the named action
  // to its handler. No status→tone/action map lives here.
  let directAction = null
  if (directRecord && directState?.banner.action === 'review-and-run')
    directAction = (
      <s-button
        disabled={builder.isDirty}
        onClick={() => {
          void builder.navigation.request(() => onStudyPrepared?.(directRecord.studyId))
        }}
      >
        {shopifyComponentCopy.studyBuilderPage.reviewAndRun}
      </s-button>
    )
  if (directRecord && directState?.banner.action === 'view-results')
    directAction = (
      <s-button href={buildDirectStudyResultsViewUrl(directRecord.studyId)}>
        {shopifyComponentCopy.studySetupCard.viewResults002}
      </s-button>
    )
  return (
    <>
      <ShopifyBackActionButton
        action={{
          content: shopifyComponentCopy.studyBuilderPage.testsBack,
          onAction: () => {
            void builder.navigation.request(onBack)
          },
        }}
      />
      {directRecord && (
        <DirectStudyDeleteControl
          shopify={shopify}
          studyId={directRecord.studyId}
          title={directRecord.draft.briefDraft.studyName}
          state={directState}
          onDeleted={() => {
            builder.handleDeleted()
            onDeleted()
          }}
          lifecycle={lifecycle}
          disabled={lifecycle.interactions.structureDisabled}
        />
      )}
      <StudyBuilderSaveBar builder={builder} />
      {directState && (
        <Banner tone={directState.banner.tone} heading={directState.label}>
          {directState.findings.map((finding) => (
            <s-paragraph key={finding.code}>{finding.message}</s-paragraph>
          ))}
          {directAction}
        </Banner>
      )}
      {builder.authoring.query.isError && (
        <Banner tone="critical">
          {shopifyComponentCopy.studyBuilderPage.statusLoadFailed}
          <s-button onClick={() => void builder.authoring.query.refetch()}>
            {shopifyComponentCopy.studySetupCard.tryAgain026}
          </s-button>
        </Banner>
      )}
      <StudyBuilderGuidance
        guide={builder.evaluation.guidance}
        sections={builder.evaluation.projection?.sections ?? []}
        lifecycle={lifecycle}
      >
        <UnifiedStudySetupPanel
          builder={builder}
          failureNotice={null}
          onBack={() => {
            void builder.navigation.request(onBack)
          }}
          sidebar={sidebar}
          bookingUrl={buildCalendlyBookingUrl({
            source: 'study_builder',
            shopDomain: scope.shopDomain,
            setupId: appView.setupId,
          })}
        />
      </StudyBuilderGuidance>
      <NextTestDraftSource seed={directRecord?.draft.nextTestSeed} />
      <StudyBuilderResearch proposal={directRecord?.draft.insightSetupSeed?.initialProposal} />
    </>
  )
}
